import React, {useMemo, useState} from 'react';
import useMeasure from 'react-use-measure';
import {a, useTransition} from '@react-spring/web';
import {ImageListItem, Skeleton, Box} from "@mui/material";
import useMedia from './useMedia';
import styles from './styles.module.css';

interface MasonryProps {
    data: any[],
    setGalleryId: (id: number) => void,
    setGalleryOpen: (open: boolean) => void,
}

function Masonry({data, setGalleryId, setGalleryOpen}: MasonryProps) {
    const columns = useMedia(
        ['(min-width: 1500px)', '(min-width: 1000px)', '(min-width: 600px)'],
        [4, 3, 2],
        1
    );
    const [ref, {width}] = useMeasure()

    const [heights, gridItems] = useMemo(() => {
        if (width <= 0) return [[0], []];
        let heights = new Array(columns).fill(0)
        let gridItems = data.map((child) => {
            const column = heights.indexOf(Math.min(...heights))
            const x = (width / columns) * column
            const y = (heights[column] += (child.height * width) / (child.width * columns)) - (child.height * width) / (child.width * columns)
            return {...child, x, y, width: width / columns, height: (child.height * width) / (child.width * columns)}
        })
        return [heights, gridItems]
    }, [columns, data, width])

    const transitions = useTransition(gridItems, {
        key: (item: any) => item.id || item.css,
        from: ({x, y, width, height}) => ({x, y, width, height, opacity: 0}),
        enter: ({x, y, width, height}) => ({x, y, width, height, opacity: 1}),
        update: ({x, y, width, height}) => ({x, y, width, height}),
        leave: {height: 0, opacity: 0},
        config: {mass: 5, tension: 100, friction: 30},
        trail: 25,
    })

    if (width === 0) {
        return <div ref={ref} style={{ width: '100%', minHeight: '50vh' }} />;
    }

    return (
        <div ref={ref} className={styles.list}
             style={{height: Math.max(...heights), width: '100%', padding: 0, margin: 0}}>
            {transitions((style, item) => (
                <a.div style={style}>
                    <ImageListItem 
                        key={item.id}
                        onClick={() => {
                            setGalleryId(item.id);
                            setGalleryOpen(true);
                        }}
                        className={"hover-effect"}
                        sx={{
                            padding: 0,
                            margin: 0,
                            cursor: 'pointer',
                            overflow: 'hidden',
                            '&:hover img': { transform: 'scale(1.05)' }
                        }}>
                        <ImageWithSkeleton src={item.css} alt={item.title} />
                    </ImageListItem>
                </a.div>
            ))}
        </div>
    )
}

function ImageWithSkeleton({ src, alt }: { src: string, alt: string }) {
    const [loaded, setLoaded] = useState(false);
    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {!loaded && (
                <Skeleton 
                    variant="rectangular" 
                    width="100%" 
                    height="100%" 
                    animation="wave"
                    sx={{ position: 'absolute', top: 0, left: 0 }}
                />
            )}
            <img
                width="100%"
                height="100%"
                src={src}
                alt={alt}
                onLoad={() => setLoaded(true)}
                style={{ 
                    display: loaded ? 'block' : 'none',
                    transition: "transform 0.4s ease-in-out",
                }}
            />
        </Box>
    );
}

export default Masonry;
